import { useCallback, useEffect, useRef, useState } from 'react'
import type { QueryError } from 'src/queries/network.utils'
import { fetchCardManagementCardDetails } from 'src/queries/physicalCardManagement/physicalCardManagement.network'
import {
  CARD_MANAGEMENT_ERROR_CODES,
  cardManagementErrorCode,
} from 'src/queries/physicalCardManagement/physicalCardManagement.utils'

import { CardHeroVariant } from './cardDetailHeroConstants'
import type { CardDetailsAvailability } from './cardDetailHeroMapping'

/** Auto-close so revealed credentials never sit exposed indefinitely. */
const AUTO_CLOSE_MS = 15000

export type CardRevealState =
  | 'idle'
  | 'loading'
  | 'ready'
  /** A failure the user may honestly retry. */
  | 'error'
  /** The service is down, not the card. Bounded retry is the correct advice. */
  | 'temporarilyUnavailable'

/**
 * Map only failures that can honestly offer a recovery action.
 *
 * `403`/`404` return no state so the disclosure closes silently: the contract's
 * client action is "do not retry", and for `403` explicitly "do not retry or
 * reveal card details".
 *
 * An unrecognised code keeps the generic retryable `error`. That is the honest
 * default for a transport failure (no response at all, so no code) — the case
 * that genuinely may succeed on a second attempt.
 */
const revealStateForErrorCode = (
  errorCode: string | undefined,
): CardRevealState | undefined => {
  switch (errorCode) {
    case CARD_MANAGEMENT_ERROR_CODES.forbidden:
    case CARD_MANAGEMENT_ERROR_CODES.notFound:
      return undefined
    case CARD_MANAGEMENT_ERROR_CODES.serviceUnavailable:
      return 'temporarilyUnavailable'
    default:
      return 'error'
  }
}

/** Feature shape isolated from the secure-details wire field names. */
export type CardRevealDetails = {
  pan: string
  expiry: string
  cvc: string
}

type CardRevealDisclosure = {
  /** The variant that currently owns the open reveal, or `null` when closed. */
  anchor: CardHeroVariant | null
  /** True when any variant's reveal is open. */
  open: boolean
  /** Secrets exist only while this is `ready`; every close clears `details`. */
  state: CardRevealState
  details: CardRevealDetails | undefined
  /** True only for the variant that currently owns the reveal. */
  isOpenFor: (variant: CardHeroVariant) => boolean
  /**
   * Toggle the reveal for a variant: open it (moving the anchor to it), or — if
   * it is already the open variant — close it.
   */
  toggle: (variant: CardHeroVariant) => void
  close: () => void
  /**
   * The outside-click / Escape container ref for a variant. Put it on the element
   * wrapping BOTH that variant's Show-details trigger and its reveal panel.
   */
  containerRefFor: (variant: CardHeroVariant) => React.RefObject<HTMLDivElement>
}

type CardRevealDisclosureOptions = {
  creditCardId: string | undefined
  cardDetailsAvailability: CardDetailsAvailability | undefined
  cardDetailsCapabilityError: boolean
  /** Plain page-owned route signal; the feature remains router-free. */
  navigationKey: string
  autoCloseMs?: number
}

/**
 * Non-modal disclosure state for the card-details reveal, owned by the hero
 * shell so the full hero and the pinned compact bar share one source of truth.
 * Both heroes stay mounted, so the reveal is *anchored* to whichever one opened
 * it: only that variant renders the panel and reports itself open. It is a
 * disclosure, not a modal — the page stays interactive and focus is not trapped.
 * Escape and a genuine outside click close it, plus an auto-close timeout and
 * the page losing visibility, so sensitive values are not left on screen — the
 * last of those because a suspended page is where the timeout stops being a
 * guarantee.
 */
export const useCardRevealDisclosure = ({
  creditCardId,
  cardDetailsAvailability,
  cardDetailsCapabilityError,
  navigationKey,
  autoCloseMs = AUTO_CLOSE_MS,
}: CardRevealDisclosureOptions): CardRevealDisclosure => {
  const revealGranted =
    !cardDetailsCapabilityError &&
    (cardDetailsAvailability === 'AVAILABLE' ||
      cardDetailsAvailability === 'READ_ONLY')
  const [anchor, setAnchor] = useState<CardHeroVariant | null>(null)
  const [state, setState] = useState<CardRevealState>('idle')
  const [details, setDetails] = useState<CardRevealDetails>()
  const fullRef = useRef<HTMLDivElement>(null)
  const compactRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<CardHeroVariant | null>(null)
  const requestGenerationRef = useRef(0)
  const abortControllerRef = useRef<AbortController>()

  const cancelRequest = useCallback((): void => {
    requestGenerationRef.current += 1
    abortControllerRef.current?.abort()
    abortControllerRef.current = undefined
  }, [])

  /** Every close path destroys the only state slot that can hold PAN or CVC. */
  const close = useCallback((): void => {
    cancelRequest()
    anchorRef.current = null
    setAnchor(null)
    setDetails(undefined)
    setState('idle')
  }, [cancelRequest])

  const toggle = useCallback(
    (variant: CardHeroVariant): void => {
      if (anchorRef.current === variant) {
        close()
        return
      }

      if (!creditCardId || !revealGranted) {
        close()
        return
      }

      cancelRequest()
      anchorRef.current = variant
      setAnchor(variant)
      setDetails(undefined)

      setState('loading')
      const requestGeneration = requestGenerationRef.current
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      // Deliberately imperative: PAN/CVC must never enter the React Query cache.
      // The event that opens the disclosure owns this one request.
      fetchCardManagementCardDetails(creditCardId, abortController.signal).then(
        (response) => {
          if (requestGenerationRef.current !== requestGeneration) return
          abortControllerRef.current = undefined
          setDetails({
            pan: response.pan,
            expiry: `${String(response.expirationMonth).padStart(2, '0')}/${String(
              response.expirationYear,
            ).slice(-2)}`,
            cvc: response.securityCode,
          })
          setState('ready')
        },
        (error: unknown) => {
          // Do not retain, stringify, log, or report the raw error: an HTTP
          // client error can carry a response body containing sensitive data,
          // and `QueryError.originalError` holds exactly that raw axios error.
          //
          // Reading the error *code* is the one safe exception. The contract
          // guarantees a strict two-field envelope — `{ error, errorCode }`,
          // with no vendor text, identifiers, or metadata — so the code is a
          // known-safe string. It is extracted here and the error object itself
          // is dropped on this line: only the string crosses into state.
          const errorCode = cardManagementErrorCode(error as QueryError)

          if (requestGenerationRef.current !== requestGeneration) return
          abortControllerRef.current = undefined
          setDetails(undefined)
          const failureState = revealStateForErrorCode(errorCode)
          if (failureState === undefined) {
            close()
            return
          }
          setState(failureState)
        },
      )
    },
    [cancelRequest, close, creditCardId, revealGranted],
  )

  const containerRefFor = useCallback(
    (variant: CardHeroVariant): React.RefObject<HTMLDivElement> =>
      variant === 'full' ? fullRef : compactRef,
    [],
  )

  // Auto-close so revealed values never sit exposed indefinitely.
  //
  // Keyed on `ready`, not on `anchor`. The anchor is set synchronously by
  // `toggle`, but nothing is on screen until the fetch resolves, so keying on it
  // would measure the window from the click and leave actual exposure at
  // `autoCloseMs − latency`.
  // A slow resolve would then flash the panel and close it, which reads as a bug
  // rather than as a security timeout. Keyed on `ready`, the window always means
  // what the constant says.
  //
  // Deliberately no timer for `loading`, `error`, or `temporarilyUnavailable`:
  // none of them put a value on screen, and all are still dismissed by every
  // ordinary path (toggle, Escape, outside click, navigation, unmount).
  useEffect(() => {
    if (state !== 'ready') return undefined
    const timer = setTimeout(close, autoCloseMs)
    return () => clearTimeout(timer)
  }, [state, autoCloseMs, close])

  // Browser suspension is the one exposure the auto-close timer cannot bound.
  //
  // `setTimeout` is not a lifetime guarantee once the page stops being current:
  // a backgrounded tab throttles timers heavily, and the back-forward cache
  // freezes them outright while retaining this React state and the rendered DOM
  // intact. A user who reveals their card number and then navigates away can
  // come back — minutes later, on a shared or unattended screen — to find the
  // plaintext still on screen, because the timer that was supposed to clear it
  // never ran. None of the ordinary dismissals fire on that path either: there
  // is no unmount, no Escape, no outside click, and no navigation signal.
  //
  // `visibilitychange` and `pagehide` are the two events that do fire, so they
  // are what the reveal closes on. `close` clears the details slot and aborts an
  // in-flight request, which matters equally: a request that resolves while the
  // page is hidden would otherwise park a secret in state for the return trip.
  // Whatever brought the page back, it comes back to `idle`.
  useEffect(() => {
    if (anchor === null) return undefined

    // Guarded on `hidden` so this fires when the page goes away, not when it
    // returns — the visible transition raises the same event.
    const onVisibilityChange = (): void => {
      if (document.hidden) close()
    }
    // `pagehide` covers the bfcache entry that `visibilitychange` can miss on
    // some browsers, and unlike `unload` it does not itself make the page
    // ineligible for caching.
    const onPageHide = (): void => close()

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [anchor, close])

  // The account shell intentionally remains mounted across nested routes. A
  // page-owned navigation signal therefore clears secrets even without unmount.
  useEffect(() => close(), [close, creditCardId, navigationKey])

  // A capability downgrade closes and clears the disclosure. UNKNOWN counts as
  // a downgrade because without a card-scoped verdict the client has no grant.
  useEffect(() => {
    if (anchorRef.current !== null && !revealGranted) close()
  }, [close, revealGranted])

  // On unmount, abort and invalidate any late completion. Component state is
  // destroyed by React; importantly, no secret is mirrored into a ref.
  useEffect(
    () => () => {
      requestGenerationRef.current += 1
      abortControllerRef.current?.abort()
      abortControllerRef.current = undefined
    },
    [],
  )

  // Escape + outside-click dismissal, attached only while open. The listener is
  // added after the opening click has finished propagating, so it never
  // self-closes on the same click that opened it.
  //
  // A click is "outside" only when it lands outside BOTH hero containers: while
  // one hero owns the reveal the other is inert, but interacting with either
  // hero must never dismiss the panel (the prototype special-cases this so the
  // swap partner can't close an open reveal). Clicks on the page beyond both
  // heroes close it.
  useEffect(() => {
    if (anchor === null) return undefined

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close()
    }
    const onDocumentClick = (event: MouseEvent): void => {
      const target = event.target as Node
      const insideAHero =
        (fullRef.current?.contains(target) ?? false) ||
        (compactRef.current?.contains(target) ?? false)
      if (!insideAHero) close()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', onDocumentClick)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('click', onDocumentClick)
    }
  }, [anchor, close])

  return {
    anchor: revealGranted ? anchor : null,
    open: revealGranted && anchor !== null,
    state: revealGranted ? state : 'idle',
    details: revealGranted ? details : undefined,
    isOpenFor: (variant) => revealGranted && anchor === variant,
    toggle,
    close,
    containerRefFor,
  }
}
